import { Form } from "antd";
import BaseModal from "components/BaseModal";
import message from "components/CustomMessage";
import Vocabulary from "components/Vocabulary";
import { MODAL_SIZE_LARGE } from "constants/Layout";
import { PermissionSetMap } from "constants/RBACConstants";
import { useFeatureFlag } from "contexts/FeatureFlagContext";
import _, { isEmpty } from "lodash";
import React, {
  ForwardedRef,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getEmptyCancellableRequest } from "restAPI";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { hasPermission } from "utils/authService";
import { FieldTypes, generateFormFields } from "utils/formUtils";

import {
  GenericIntegrationTicketDefaultValues,
  genericIntegrationTicketProductSubproductAppendAll,
} from "../genericIntegrationTicketConstants";
import {
  generateResponse,
  integrationTicketingToolsProductOptionsMutator,
  useUnifiedMultiTemplateOptions,
  useUnifiedSingleTemplateOptions,
} from "../genericIntegrationTicketUtils";

type RepoOptionType = {
  id: number;
  name: string;
};

const INITIAL_VALUES = {
  product: ["all"],
  subProduct: ["all"],
};

function getInitialData(
  data: GenericIntegrationTicketDefaultValues = {},
): GenericIntegrationTicketDefaultValues {
  if (!isEmpty(data) && data.projectKey) {
    data = {
      ...data,
      subProduct: !isEmpty(data.subProductIds) ? data.subProductIds : ["all"],
      product: !isEmpty(data.product) ? data.product : ["all"],
      repo: data.projectKey,
    };

    return data;
  }
  return INITIAL_VALUES;
}

type GitHubIssuesMappingFormProps = {
  loginConfigId: number;
  onUpdateConfigComponent: (value: boolean) => void;
  config?: GenericIntegrationTicketDefaultValues;
  url?: string;
  name: string;
  afterClose?: () => void;
};

const GitHubIssuesMappingForm = (
  {
    loginConfigId,
    onUpdateConfigComponent,
    name,
    ...props
  }: GitHubIssuesMappingFormProps,
  ref: ForwardedRef<any>,
) => {
  const [initialData] = useState(() => getInitialData(props.config));
  const isEdit = Boolean(props.config?.id);
  const [form] = Form.useForm();
  const [data, setData] = useState(initialData);
  const featureFlags = useFeatureFlag();
  const { getUnifiedSingleTemplates, unifiedSingleDefaultTemplate } =
    useUnifiedSingleTemplateOptions();
  const { getUnifiedMultiTemplates, unifiedMultiDefaultTemplate } =
    useUnifiedMultiTemplateOptions();
  const toolName = name.toUpperCase();
  const repoRef = useRef<RepoOptionType[]>([]);
  useEffect(() => {
    if (!isEdit) {
      form.setFieldsValue({
        ticketUnifiedSingleTemplateId: unifiedSingleDefaultTemplate,
        ticketUnifiedMultipleTemplateId: unifiedMultiDefaultTemplate,
      });
    }
  }, [isEdit, form, unifiedSingleDefaultTemplate, unifiedMultiDefaultTemplate]);

  const getRepos = useCallback(
    (params) => {
      if (!loginConfigId) {
        return getEmptyCancellableRequest([]);
      }
      const { promise, cancel } = ToolsIntegrationsAPI.getRepoDetails(
        loginConfigId,
        params,
      );
      promise
        .then((data) => {
          repoRef.current = !isEmpty(repoRef.current)
            ? _.uniqBy([...repoRef.current, ...data.content], "id")
            : data.content;
        })
        .catch(() => {
          repoRef.current = [];
        });
      return { promise, cancel };
    },
    [loginConfigId],
  );

  const onOk = async () => {
    await form.validateFields();
    const formData = {
      ...form.getFieldsValue(),
      ticketSystemType: "GITHUB_ISSUES",
    };
    formData.ticketClosureStatus = _.isEmpty(formData.ticketClosureStatus)
      ? null
      : formData.ticketClosureStatus;
    formData.customFields = [];
    const configValues: any = await generateResponse(
      formData,
      featureFlags,
      toolName,
      props.config?.id,
      loginConfigId,
    );
    const payload = _.omit(
      {
        ...configValues.configValues,
        projectKey: configValues.configValues.repo,
        gitInstallationId: loginConfigId,
        repoId: _.find(repoRef.current, {
          name: configValues.configValues.repo,
        })?.id,
      },
      ["repo", "loginConfigId"],
    );
    await ToolsIntegrationsAPI[
      !isEdit ? "integrateTicketSystemTool" : "updateTicketSystemToolMapping"
    ](payload, props.config?.id).promise;
    message.success(`GitHub Mapping saved`);
    setData({});
    onUpdateConfigComponent && onUpdateConfigComponent(true);
  };

  const resetSubProductsField = useCallback(
    (values) => {
      setData((data: GenericIntegrationTicketDefaultValues) => ({
        ...data,
        product: values,
      }));
      form.setFieldsValue({ subProduct: ["all"] });
    },
    [form],
  );

  const modalProps = {
    title: `${isEdit ? "Edit" : "Add"} GitHub Mapping`,
    width: MODAL_SIZE_LARGE,
    onOk,
    disabled: !hasPermission(
      isEdit ? PermissionSetMap.EDIT_TOOL_CONFIG : PermissionSetMap.WRITE_TOOLS,
    ),
  };

  // if more than one product or All product is selected then subproduct should be disabled
  // this would work as previous, just will get the product id from array, as product is multi select
  const productId =
    data.product && data.product[0] !== "all" ? data.product[0] : null;

  const onCreateSubproduct = useCallback(
    (createdSubproduct) => {
      const subProductValues = form.getFieldValue("subProduct");
      form.setFieldsValue({
        subProduct: [...subProductValues, createdSubproduct.id],
      });
      setData((prevdata) => ({
        ...prevdata,
        subProduct: [...subProductValues, createdSubproduct.id],
      }));
    },
    [form],
  );

  const onCreateProduct = useCallback(
    (createdProduct) => {
      const productsAfterCreate = [
        ...form.getFieldValue("product"),
        createdProduct.id,
      ];
      form.setFieldsValue({
        product: productsAfterCreate,
        subProduct: ["all"],
      });
      setData((data) => ({
        ...data,
        product: productsAfterCreate,
        subProduct: ["all"],
      }));
    },
    [form],
  );

  const productSubProductFields = useMemo(
    () => [
      {
        name: "product",
        label: <Vocabulary name="product" />,
        type: FieldTypes.PROD_SUBPROD_SELECT_ADD,
        required: true,
        colSpan: 12,
        // disabled:isEdit,
        props: {
          dropdownType: "product",
          showAddButton: true,
          optionsMutator: (
            options: [{ label: string; value: number | string }],
          ) =>
            integrationTicketingToolsProductOptionsMutator(
              options,
              form.getFieldValue("product"),
            ),
          onChange: resetSubProductsField,
          onCreate: onCreateProduct,
          mode: "multiple",
        },
      },
      {
        name: "subProduct",
        label: <Vocabulary name="subProduct" />,
        type: FieldTypes.PROD_SUBPROD_SELECT_ADD,
        required: true,
        colSpan: 12,
        disabled: data.product && data.product.length > 1,
        props: {
          productId: productId,
          showAddButton: Boolean(productId),
          dropdownType: "subProduct",
          onCreate: onCreateSubproduct,
          onChange: (values: string[]) => {
            if (values.includes("all")) {
              form.setFieldsValue({ subProduct: ["all"] });
            }
          },
          optionsMutator: (options: any[]) => {
            const updatedOptions =
              genericIntegrationTicketProductSubproductAppendAll(options);
            const selectedValues = form.getFieldValue("subProduct") ?? [];
            const isAllSelected = selectedValues.includes("all");

            return updatedOptions.map((option) => ({
              ...option,
              disabled: isAllSelected && option.value !== "all",
            }));
          },

          mode: "multiple",
        },
      },
    ],
    [
      form,
      productId,
      resetSubProductsField,
      onCreateSubproduct,
      onCreateProduct,
      data.product,
    ],
  );

  const fields = useMemo(
    () => [
      ...productSubProductFields,
      {
        name: "repo",
        label: "Repo",
        colSpan: 12,
        type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
        required: true,
        props: {
          api: getRepos,
          resetOptionsProp: getRepos,
          parseSelectOptions: (options: RepoOptionType[]) =>
            options.map((option) => ({
              label: option.name,
              value: option.name,
            })),
          onChange: (value: string) => {
            setData((data: GenericIntegrationTicketDefaultValues) => ({
              ...data,
              projectKey: value,
            }));
          },
        },
      },
      ...(featureFlags.UNIFIED_TICKET_TEMPLATE
        ? [
            {
              name: "ticketUnifiedSingleTemplateId",
              label: "Single Finding Template",
              colSpan: 12,
              type: FieldTypes.SELECT,
              props: {
                api: getUnifiedSingleTemplates,
              },
            },
            {
              name: "ticketUnifiedMultipleTemplateId",
              label: "Multi-Finding Template",
              colSpan: 12,
              type: FieldTypes.SELECT,
              props: {
                api: getUnifiedMultiTemplates,
              },
            },
          ]
        : []),
    ],
    [
      getRepos,
      productSubProductFields,
      featureFlags.UNIFIED_TICKET_TEMPLATE,
      getUnifiedSingleTemplates,
      getUnifiedMultiTemplates,
    ],
  );

  return (
    <BaseModal ref={ref} modalProps={modalProps} afterClose={props.afterClose}>
      <Form form={form} initialValues={data} layout="vertical">
        {generateFormFields(fields)}
      </Form>
    </BaseModal>
  );
};

export default forwardRef(GitHubIssuesMappingForm);
